import { ClockIcon } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';

import { minuteStepFor, timeToValues } from './segments';
import { parseTime } from './temporal';
import { TemporalField, type TemporalInputProps, type TemporalPanelApi } from './temporal-field';
import { TimeColumns } from './time-columns';
import { isTimeOptionDisabled, pickTimeOption, type TimeBounds } from './time-range';

export interface TimePickerProps extends TemporalInputProps {
  /** Seconds, as on the native input (default 60). Minutes step by `step / 60`. */
  step?: number;
}

/**
 * A themed `<input type="time">`: `value` is `HH:mm` (or `''`), `min`/`max` too. Drop it in where
 * `<Input type="time" {...field} />` was.
 */
export function TimePicker({ step, ...props }: TimePickerProps) {
  const { t } = useTranslation('common');
  const bounds: TimeBounds = { min: parseTime(props.min), max: parseTime(props.max) };
  return (
    <TemporalField
      {...props}
      kind="time"
      minuteStep={minuteStepFor(step)}
      triggerIcon={<ClockIcon aria-hidden />}
      triggerLabel={t('dateTime.chooseTime')}
      renderPanel={(api) => <TimePanel api={api} bounds={bounds} />}
    />
  );
}

function TimePanel({ api, bounds }: { api: TemporalPanelApi; bounds: TimeBounds }) {
  return (
    <TimeColumns
      className="h-56"
      locale={api.locale}
      options={api.options}
      values={api.segments}
      focusRef={api.initialFocusRef}
      isDisabled={(type, option) =>
        isTimeOptionDisabled(type, option, api.segments, api.options, bounds)
      }
      onPick={(type, option) => {
        const time = pickTimeOption(type, option, api.segments, api.options, bounds);
        api.update(timeToValues(time, api.options));
      }}
      onEnter={api.close}
    />
  );
}
